import { c as _c } from "react/compiler-runtime";
import { Drawer, Flex, FlexProps } from '@chakra-ui/react';
import { chakra } from '@chakra-ui/react';
import { css, Global } from '@emotion/react';
import { useIsMounted } from '@react-hookz/web';
import { useParentAppLayout } from '@src/components/sco/AppLayout/AppLayoutContext';
import { AppLayoutPanelProvider, useParentAppLayoutPanel } from '@src/components/sco/AppLayout/Panel/AppLayoutPanelContext';
import { AppLayoutResizeHandle } from '@src/components/sco/AppLayout/ResizeHandle';
import { Dimensions, useResize } from '@src/components/sco/AppLayout/ResizeHandle/useResize';
import { BreakpointContextProvider, useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { motion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import React, { ReactNode, useEffect, useRef } from 'react';
export const MotionBox = chakra(motion.div) as React.ComponentType<any>;
export type AppLayoutPanelProps = {
  id?: string;
  children?: ReactNode;
  containerProps?: FlexProps;
  /** Merged onto the inner BreakpointContextProvider box (overflow, …). */
  innerBoxProps?: FlexProps;
  defaultWidth?: number | (() => number);
  drawerPlacement?: 'left' | 'right' | string;
  isDrawerOpen?: boolean;
  isPanelOpen?: boolean;
  isResizable?: boolean;
  isUsingDrawer?: boolean;
  maxWidth?: number;
  minWidth?: number;
  scrollable?: boolean;
  onDrawerClose?: () => void;
  onPanelClose?: () => void;
  onResize?: (size: Dimensions) => void;
  resizeHandlePosition?: 'left' | 'right' | string;
  resetScrollOnPathChange?: boolean;
  useDrawerBreakpoints?: any | {
    base?: boolean;
    sm?: boolean;
    md?: boolean;
    lg?: boolean;
    xl?: boolean;
  };
};
export const AppLayoutPanel: React.FC<AppLayoutPanelProps> = props => {
  const $ = _c(101);
  const layout = useParentAppLayout();
  const panel = useParentAppLayoutPanel();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const isMounted = useIsMounted();
  const pathname = usePathname();
  const scrollRef = useRef(null);
  let t0;
  if ($[0] !== getBreakpointValue || $[1] !== props.useDrawerBreakpoints) {
    t0 = props.useDrawerBreakpoints ? getBreakpointValue(props.useDrawerBreakpoints) : false;
    $[0] = getBreakpointValue;
    $[1] = props.useDrawerBreakpoints;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const useDrawer = t0;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let namedContainerProps = t1;
  if (props.id?.endsWith("-main")) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        bgColor: "transparent",
        boxShadow: "none",
        border: "1px solid {colors.colorPalette.border}"
      };
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    namedContainerProps = t2;
  }
  if (props.id?.endsWith("-nav")) {
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        bgColor: "transparent",
        boxShadow: "none",
        border: "1px solid transparent",
        padding: [3, 3, 0]
      };
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    namedContainerProps = t2;
  }
  if (props.id?.endsWith("-aside")) {
    let t2;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        bgColor: "transparent",
        boxShadow: "none",
        border: "1px solid {colors.colorPalette.border/50}"
      };
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    namedContainerProps = t2;
  }
  let t2;
  if ($[7] !== layout || $[8] !== panel || $[9] !== props.id || $[10] !== useDrawer) {
    t2 = () => {
      layout.updatePanel?.(props.id as string, {
        isUsingDrawer: useDrawer ? true : false,
        isDrawerOpen: false
      });
      if (layout && panel && useDrawer) {
        layout.closePanel?.(panel.id);
      }
    };
    $[7] = layout;
    $[8] = panel;
    $[9] = props.id;
    $[10] = useDrawer;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== useDrawer) {
    t3 = [useDrawer];
    $[12] = useDrawer;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  useEffect(t2, t3);
  let t4;
  if ($[14] !== props.resetScrollOnPathChange) {
    t4 = () => {
      if (props.resetScrollOnPathChange && scrollRef.current) {
        scrollRef.current.scrollTo(0, 0);
      }
    };
    $[14] = props.resetScrollOnPathChange;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== pathname) {
    t5 = [pathname];
    $[16] = pathname;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  useEffect(t4, t5);
  let t6;
  if ($[18] !== props.defaultWidth) {
    t6 = typeof props.defaultWidth === "function" ? props.defaultWidth() : props.defaultWidth || 300;
    $[18] = props.defaultWidth;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const t7 = props.minWidth || 0;
  let t8;
  if ($[20] !== props.isResizable || $[21] !== props.maxWidth || $[22] !== props.onResize || $[23] !== props.resizeHandlePosition || $[24] !== t6 || $[25] !== t7) {
    t8 = {
      defaultWidth: t6,
      minWidth: t7,
      maxWidth: props.maxWidth,
      isResizable: props.isResizable,
      handlePosition: props.resizeHandlePosition,
      onResize: props.onResize
    };
    $[20] = props.isResizable;
    $[21] = props.maxWidth;
    $[22] = props.onResize;
    $[23] = props.resizeHandlePosition;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const resize = useResize(t8);
  let motionVariants;
  if ($[27] !== props.resizeHandlePosition || $[28] !== resize) {
    motionVariants = {
      enter: {
        marginLeft: 0,
        marginRight: 0,
        transition: {
          type: "spring",
          duration: 0.5,
          bounce: 0.25
        }
      },
      exit: {
        marginLeft: 0,
        marginRight: 0,
        transition: {
          type: "ease-in",
          duration: 0.25,
          bounce: 0.25
        }
      }
    };
    if (props.resizeHandlePosition === "left") {
      motionVariants.enter.marginRight = 0;
      motionVariants.exit.marginRight = -resize.width;
    }
    if (props.resizeHandlePosition === "right") {
      motionVariants.enter.marginLeft = 0;
      motionVariants.exit.marginLeft = -resize.width;
    }
    $[27] = props.resizeHandlePosition;
    $[28] = resize;
    $[29] = motionVariants;
  } else {
    motionVariants = $[29];
  }
  let t9;
  if ($[30] !== layout || $[31] !== props) {
    t9 = () => {
      layout.closeDrawer?.(props.id as string);
      if (props.onDrawerClose) {
        props.onDrawerClose();
      }
    };
    $[30] = layout;
    $[31] = props;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const onDrawerClose = t9;
  if (useDrawer) {
    const isLeft = props.drawerPlacement === "left" || props.resizeHandlePosition === "left";
    const t10 = props.id as string;
    const t11 = props.isDrawerOpen === true;
    const t12 = props.drawerPlacement as any || (props.resizeHandlePosition === "left" ? "end" : "start");
    let t13;
    if ($[33] !== onDrawerClose) {
      t13 = e => {
        if (!e.open) {
          onDrawerClose();
        }
      };
      $[33] = onDrawerClose;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    let t14;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <Drawer.Backdrop backdropFilter="blur(10px)" />;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    const t15 = isLeft ? 0 : undefined;
    const t16 = !isLeft ? 0 : undefined;
    let t17;
    if ($[36] !== t15 || $[37] !== t16) {
      t17 = <Drawer.CloseTrigger left={t15} margin={4} position="absolute" right={t16} top={0} zIndex={9999} />;
      $[36] = t15;
      $[37] = t16;
      $[38] = t17;
    } else {
      t17 = $[38];
    }
    let t18;
    if ($[39] !== namedContainerProps || $[40] !== props.children || $[41] !== props.containerProps) {
      t18 = <Flex align="stretch" flexDirection="column" flexGrow={1} justify="stretch" overflowY="scroll" {...namedContainerProps} {...props.containerProps}>{props.children}</Flex>;
      $[39] = namedContainerProps;
      $[40] = props.children;
      $[41] = props.containerProps;
      $[42] = t18;
    } else {
      t18 = $[42];
    }
    let t19;
    if ($[43] !== t17 || $[44] !== t18) {
      t19 = <Drawer.Positioner><Drawer.Content maxWidth="85%" position="relative" width="85%">{t17}{t18}</Drawer.Content></Drawer.Positioner>;
      $[43] = t17;
      $[44] = t18;
      $[45] = t19;
    } else {
      t19 = $[45];
    }
    let t20;
    if ($[46] !== t11 || $[47] !== t12 || $[48] !== t13 || $[49] !== t19) {
      t20 = <Drawer.Root closeOnEscape={true} closeOnInteractOutside={true} open={t11} placement={t12} onOpenChange={t13}>{t14}{t19}</Drawer.Root>;
      $[46] = t11;
      $[47] = t12;
      $[48] = t13;
      $[49] = t19;
      $[50] = t20;
    } else {
      t20 = $[50];
    }
    let t21;
    if ($[51] !== t10 || $[52] !== t20) {
      t21 = <AppLayoutPanelProvider id={t10}>{t20}</AppLayoutPanelProvider>;
      $[51] = t10;
      $[52] = t20;
      $[53] = t21;
    } else {
      t21 = $[53];
    }
    return t21;
  }
  if (props.isResizable || !!props.resizeHandlePosition) {
    const t10 = props.id as string;
    let t11;
    if ($[54] !== props.isPanelOpen || $[55] !== props.resizeHandlePosition || $[56] !== resize) {
      t11 = props.isPanelOpen !== false && props.resizeHandlePosition === "left" && <AppLayoutResizeHandle {...resize.getHandleProps()} />;
      $[54] = props.isPanelOpen;
      $[55] = props.resizeHandlePosition;
      $[56] = resize;
      $[57] = t11;
    } else {
      t11 = $[57];
    }
    const t12 = !isMounted ? null : props.isPanelOpen !== false ? "enter" : "exit";
    const t13 = props.isPanelOpen === false ? "exit" : "enter";
    const t14 = motionVariants as any;
    const t15 = namedContainerProps;
    const t16 = props.containerProps;
    let t17;
    if ($[58] !== resize) {
      t17 = resize.getContainerProps();
      $[58] = resize;
      $[59] = t17;
    } else {
      t17 = $[59];
    }
    let t18;
    if ($[60] !== props.innerBoxProps) {
      t18 = {
        align: "stretch",
        alignSelf: "stretch",
        justifySelf: "stretch",
        display: "flex",
        justify: "stretch",
        height: "full",
        width: "full",
        ...props.innerBoxProps
      };
      $[60] = props.innerBoxProps;
      $[61] = t18;
    } else {
      t18 = $[61];
    }
    let t19;
    if ($[62] !== props.children || $[63] !== t18) {
      t19 = <BreakpointContextProvider boxProps={t18}>{props.children}</BreakpointContextProvider>;
      $[62] = props.children;
      $[63] = t18;
      $[64] = t19;
    } else {
      t19 = $[64];
    }
    let t20;
    if ($[65] !== namedContainerProps || $[66] !== props.containerProps || $[67] !== t12 || $[68] !== t13 || $[69] !== t14 || $[70] !== t17 || $[71] !== t19) {
      t20 = <MotionBox alignSelf="stretch" animate={t12} bgColor="colorPalette.panel" border="1px solid {colors.colorPalette.border/25}" borderRadius="lg" display="flex" flexShrink={1} initial={t13} justifySelf="stretch" overflowY="hidden" variants={t14} zIndex="1" {...t15} {...t16} {...t17}>{t19}</MotionBox>;
      $[65] = namedContainerProps;
      $[66] = props.containerProps;
      $[67] = t12;
      $[68] = t13;
      $[69] = t14;
      $[70] = t17;
      $[71] = t19;
      $[72] = t20;
    } else {
      t20 = $[72];
    }
    let t21;
    if ($[73] !== props.isPanelOpen || $[74] !== props.resizeHandlePosition || $[75] !== resize) {
      t21 = props.isPanelOpen !== false && props.resizeHandlePosition === "right" && <AppLayoutResizeHandle {...resize.getHandleProps()} />;
      $[73] = props.isPanelOpen;
      $[74] = props.resizeHandlePosition;
      $[75] = resize;
      $[76] = t21;
    } else {
      t21 = $[76];
    }
    let t22;
    if ($[77] !== resize.isResizing) {
      t22 = resize.isResizing && <Global styles={css`
              body * {
                -user-select: none;
                -webkit-user-select: none; /* Chrome/Safari */
                -moz-user-select: none; /* Firefox */
                -ms-user-select: none; /* IE10+ */
              }
            `} />;
      $[77] = resize.isResizing;
      $[78] = t22;
    } else {
      t22 = $[78];
    }
    let t23;
    if ($[79] !== t10 || $[80] !== t11 || $[81] !== t20 || $[82] !== t21 || $[83] !== t22) {
      t23 = <AppLayoutPanelProvider id={t10}>{t11}{t20}{t21}{t22}</AppLayoutPanelProvider>;
      $[79] = t10;
      $[80] = t11;
      $[81] = t20;
      $[82] = t21;
      $[83] = t22;
      $[84] = t23;
    } else {
      t23 = $[84];
    }
    return t23;
  } else {
    const t10 = props.id as string;
    const t11 = props.scrollable === false ? "full" : "auto";
    const t12 = `${props.id}-inner`;
    const t13 = props.minWidth ?? 0;
    const t14 = props.scrollable === false ? "hidden" : "auto";
    let t15;
    if ($[85] !== props.innerBoxProps) {
      t15 = {
        alignSelf: "stretch",
        display: "flex",
        height: "full",
        justifySelf: "stretch",
        minWidth: 0,
        overflowY: "auto",
        width: "full",
        ...props.innerBoxProps
      };
      $[85] = props.innerBoxProps;
      $[86] = t15;
    } else {
      t15 = $[86];
    }
    let t16;
    if ($[87] !== props.children || $[88] !== t15) {
      t16 = <BreakpointContextProvider boxProps={t15}>{props.children}</BreakpointContextProvider>;
      $[87] = props.children;
      $[88] = t15;
      $[89] = t16;
    } else {
      t16 = $[89];
    }
    let t17;
    if ($[90] !== props.containerProps || $[91] !== props.maxWidth || $[92] !== t11 || $[93] !== t12 || $[94] !== t13 || $[95] !== t14 || $[96] !== t16) {
      t17 = <Flex ref={scrollRef} align="stretch" alignSelf="stretch" bgColor="colorPalette.panel" border="1px solid {colors.colorPalette.border/25}" borderRadius="lg" flex={1} height={t11} id={t12} justify="stretch" justifySelf="stretch" maxWidth={props.maxWidth} minWidth={t13} overflowX="hidden" overflowY={t14} {...props.containerProps}>{t16}</Flex>;
      $[90] = props.containerProps;
      $[91] = props.maxWidth;
      $[92] = t11;
      $[93] = t12;
      $[94] = t13;
      $[95] = t14;
      $[96] = t16;
      $[97] = t17;
    } else {
      t17 = $[97];
    }
    let t18;
    if ($[98] !== t10 || $[99] !== t17) {
      t18 = <AppLayoutPanelProvider id={t10}>{t17}</AppLayoutPanelProvider>;
      $[98] = t10;
      $[99] = t17;
      $[100] = t18;
    } else {
      t18 = $[100];
    }
    return t18;
  }
};